import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Box, styled, Toolbar, IconButton, Divider } from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  FormatClear,
} from "@mui/icons-material";

interface EditorProps {
  text: string;
  handleChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  height?: number;
  backgroundColor?: string;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

const StyledEditorContainer = styled(Box)<{ bgcolor?: string }>(
  ({ theme, bgcolor }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: bgcolor || "inherit",
    overflow: "hidden",
  }),
);

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(0.5, 1),
  minHeight: "auto",
  gap: theme.spacing(0.5),
}));

const StyledEditorContent = styled(EditorContent)<{ height?: number }>(
  ({ theme, height }) => ({
    "& .ProseMirror": {
      outline: "none",
      padding: theme.spacing(2),
      fontSize: "16px",
      fontFamily: theme.typography.body1.fontFamily,
      lineHeight: 1.6,
      minHeight: height ? `${height}px` : "200px",
      maxHeight: "400px",
      overflowY: "auto",
      textAlign: "left",
      "&:focus": {
        outline: "none",
      },
    },
    "& .ProseMirror p.is-editor-empty:first-child::before": {
      content: "attr(data-placeholder)",
      float: "left",
      color: theme.palette.text.secondary,
      fontStyle: "italic",
      pointerEvents: "none",
      height: 0,
    },
    "& .ProseMirror ul": {
      paddingLeft: theme.spacing(3),
    },
    "& .ProseMirror ol": {
      paddingLeft: theme.spacing(3),
    },
    "& .ProseMirror a": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
      "&:hover": {
        textDecoration: "none",
      },
    },
  }),
);

export default function TextEditor({
  text,
  handleChange,
  placeholder = "Write something about yourself...",
  maxLength = 1200,
  height = 200,
  backgroundColor,
  onKeyDown,
}: EditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "editor-link",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: text,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const textContent = editor.getText();
      
      if (textContent.length <= maxLength) {
        handleChange(html);
      }
    },
    editorProps: {
      handleKeyDown: (view, event) => {
        // Handle Ctrl+Enter or Cmd+Enter
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
          event.preventDefault();
          if (onKeyDown) {
            onKeyDown(event as any);
          }
          return true;
        }
        
        // Call original onKeyDown for other keys
        if (onKeyDown) {
          onKeyDown(event as any);
        }
        
        return false;
      },
    },
  });

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt("Enter URL:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <StyledEditorContainer bgcolor={backgroundColor}>
      <StyledToolbar>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          color={editor.isActive("bold") ? "primary" : "default"}
        >
          <FormatBold />
        </IconButton>
        
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          color={editor.isActive("italic") ? "primary" : "default"}
        >
          <FormatItalic />
        </IconButton>
        
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          color={editor.isActive("underline") ? "primary" : "default"}
        >
          <FormatUnderlined />
        </IconButton>
        
        <Divider orientation="vertical" flexItem />
        
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          color={editor.isActive("bulletList") ? "primary" : "default"}
        >
          <FormatListBulleted />
        </IconButton>
        
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          color={editor.isActive("orderedList") ? "primary" : "default"}
        >
          <FormatListNumbered />
        </IconButton>
        
        <Divider orientation="vertical" flexItem />
        
        <IconButton
          size="small"
          onClick={addLink}
          color={editor.isActive("link") ? "primary" : "default"}
        >
          <LinkIcon />
        </IconButton>
        
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        >
          <FormatClear />
        </IconButton>
      </StyledToolbar>
      
      <StyledEditorContent editor={editor} height={height} />
    </StyledEditorContainer>
  );
}

